import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '#store/api';
import { DataApiResponse, DataCollectionApiResponse } from './types';

export const FAMILY_ASSISTANCE_KEY = 'familyAssistance';

export interface FamilyAssistanceBase {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  lookingForFirstName: string;
  lookingForLastName: string;
  familyMembersCount: number;
  childrenCount?: number;
  counselorPreferences: number[];
  needsChildcare: boolean;
  hasDisabilities: boolean;
  specifyDisabilities?: number[];
  needs: number[]; 
  additionalNeeds: string;
  incidentId: string;
  photoUrl?: string;
  photoId?: string;
}

export interface FamilyAssistance extends FamilyAssistanceBase {
  id: string;
}

interface CreateFamilyAssistanceParams {
  familyAssistance: FamilyAssistanceBase;
}

interface UpdateFamilyAssistanceParams {
  id: string;
  familyAssistance: FamilyAssistanceBase;
}

export interface GetFamilyAssistanceByIdParams {
  id: string;
}

interface GetFamilyAssistancesParams {
  incidentId?: string;
  searchQuery?: string;
  skip: number;
  take?: number;
}

interface DeleteFamilyAssistanceParams {
  id: string;
}

export const useFamilyAssistances = (params: GetFamilyAssistancesParams) => {
  return useQuery({
    queryKey: [FAMILY_ASSISTANCE_KEY, params],
    queryFn: async () => {
      const response = await apis.familyAssistance.getFamilyAssistanceList({ params });
      return response.data as DataCollectionApiResponse<FamilyAssistance>;
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};

export const useFamilyAssistanceById = (params: GetFamilyAssistanceByIdParams) => {
  return useQuery({
    queryKey: [FAMILY_ASSISTANCE_KEY, params],
    queryFn: async () => {
      const response = await apis.familyAssistance.getFamilyAssistanceById({ data: params });
      return response.data as DataApiResponse<FamilyAssistance>;
    },
  });
};

export const useCreateFamilyAssistance = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, CreateFamilyAssistanceParams>({
    mutationFn: async (params: CreateFamilyAssistanceParams) => {
      const response = await apis.familyAssistance.createFamilyAssistance({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FAMILY_ASSISTANCE_KEY] });
    },
  });
};


export const useUpdateFamilyAssistance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateFamilyAssistanceParams) => {
      const response = await apis.familyAssistance.updateFamilyAssistance({ data: params });
      return response.data as DataApiResponse<FamilyAssistance>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FAMILY_ASSISTANCE_KEY] });
    },
  });
};


export const useDeleteFamilyAssistance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteFamilyAssistanceParams) => {
      const response = await apis.familyAssistance.deleteFamilyAssistance({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FAMILY_ASSISTANCE_KEY] });
    },
  });
};
